import React from 'react';
import { Box, Button, Flex, Middot, Tooltip, Typography } from '@fullstory/components';
import { PageDefinition } from '@fullstory/proto/fs/api/pages';
import { domainsToString, pathFromPatternRule } from '@fullstory/library-ui';
import { RuleWarning } from '../RuleWarning';
import { ValidationError } from '@fullstory/proto/fs/services/paige';
import { FormattedMessage } from 'react-intl';
import { intl } from '@fullstory/intl';
export interface ConflictingRulesProps {
  errors: ValidationError[];
  onEdit: (pageId: number | null) => void;
  onArchive: (id: number, archive: boolean) => void;
  getPageById: (pageDefId: number) => PageDefinition | undefined;
}
export const conflictWithSelfMessage = intl.formatMessage({
  defaultMessage:
    'The URL patterns overlap with another you’re trying to save. To continue, change or remove the URL rule.',
  id: 'luh1Pd',
});
export const conflictWithExistingRuleMessage = intl.formatMessage({
  defaultMessage:
    'This URL pattern overlaps with an existing Page. To continue, change the URL rule or archive the existing page.',
  id: '/ZFfXV',
});
const editTooltipContent = intl.formatMessage({
  defaultMessage:
    'Clicking Edit will load this Page’s definition in the modal, losing any unsaved changes.',
  id: '5JSI5C',
});

const getPageNameById = (
  getPageById: (pageDefId: number) => PageDefinition | undefined,
  pageId: number | undefined,
) => {
  if (pageId) {
    const pageDefinition = getPageById(pageId);
    if (pageDefinition?.name) {
      return pageDefinition.name;
    }
  }
  return intl.formatMessage({ defaultMessage: 'Unnamed', id: 'uq4QAg' });
};

export const ConflictingRules: React.FC<ConflictingRulesProps> = ({
  errors,
  onEdit,
  onArchive,
  getPageById,
}) => {
  const selfConflicts = errors.filter(error => error.selfConflict);
  const patternErrors = errors.filter(error => error.pattern);
  const containsSelfConflict = selfConflicts.length > 0;

  if (selfConflicts.length === 0 && patternErrors.length === 0) {
    return null;
  }

  return (
    <RuleWarning
      text={containsSelfConflict ? conflictWithSelfMessage : conflictWithExistingRuleMessage}
    >
      {!containsSelfConflict &&
        patternErrors.map(conflict => (
          <Flex key={conflict.pattern?.existingPage} testId="conflicting-rule">
            <Typography variant="body2" fontWeight="bold">
              {getPageNameById(getPageById, conflict?.pattern?.existingPage)}
            </Typography>
            <Box margin={[0, 8, 0, 8]}>
              <Middot />
            </Box>
            <Typography variant="body2">
              {domainsToString(conflict.pattern?.existingPattern?.domains) +
                pathFromPatternRule(conflict!.pattern?.existingPattern ?? null)}
            </Typography>
            <Box margin={[0, 8, 0, 8]}>
              <Middot />
            </Box>
            <Tooltip
              placement="top"
              tooltipContent={
                <Typography variant="body2" color="neutrals-n0">
                  {editTooltipContent}
                </Typography>
              }
            >
              <Button variant="minimal" onClick={() => onEdit(conflict?.pattern?.existingPage!)}>
                <FormattedMessage defaultMessage="Edit" id="wEQDC6" />
              </Button>
            </Tooltip>
            <Box margin={[0, 8, 0, 8]}>
              <Middot />
            </Box>
            <Button
              variant="minimal"
              onClick={() => onArchive(conflict?.pattern?.existingPage!, true)}
            >
              <FormattedMessage defaultMessage="Archive" id="hrgo+E" />
            </Button>
          </Flex>
        ))}
    </RuleWarning>
  );
};
